import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SeoTags } from "../../components/Head/SeoTags";
import { LogoOnly } from "../../assets/icons/logoOnly";
import TrademarkDisclaimer from "../../components/Legal/TrademarkDisclaimer";

// Bespoke Instapaper page (not the shared CompetitorAlternative template) so we
// can meet Instapaper on its own turf: it's a read-it-later app, so the offline
// reading + reading-list angle is front and centre, mirroring the depth of the
// Pocket page. English-only (localized={false}) — content is hardcoded, so it's
// listed in scripts/routes.js NON_LOCALIZED_ROUTES and gets no locale prefix.

const faqData = [
  {
    question: "Can I import my Instapaper saves into onBookmarks?",
    answer:
      "Yes. Export your saves from Instapaper (Settings → Export → Download .CSV), then upload the file in onBookmarks. Your articles come across with their titles and links in a couple of minutes.",
  },
  {
    question: "Is onBookmarks free?",
    answer:
      "Yes. The free plan includes unlimited bookmarks, AI tagging, multi-collection organization, and browser extensions. Instapaper gates unlimited notes, full-text search, and speed reading behind its paid tier.",
  },
  {
    question: "Does onBookmarks work offline like Instapaper?",
    answer:
      "Yes. Your saved articles are cached on your device so you can read them without a connection, and a built-in reading list keeps your unread queue tidy — the read-it-later workflow Instapaper is known for.",
  },
  {
    question: "Is Instapaper shutting down?",
    answer:
      "No — Instapaper is still running. But it has changed hands several times and its feature set has stayed largely the same for years. onBookmarks gives you an actively developed home with AI organization and encryption on top of read-it-later.",
  },
  {
    question: "How is onBookmarks different from a read-it-later app?",
    answer:
      "Instapaper is built around a single reading queue. onBookmarks is a full bookmark manager: save any page, organize it across multiple collections and Spaces, let AI tag it automatically, and still read it later offline.",
  },
  {
    question: "Are my bookmarks private?",
    answer:
      "You can encrypt sensitive bookmarks with client-side AES-256 encryption, so they're scrambled on your device before they ever reach our servers. Instapaper stores your saves in plain text.",
  },
];

const InstapaperAlternative = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#migration-guide") {
      const element = document.getElementById("migration-guide");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  return (
    <>
      <SeoTags
        localized={false}
        title="Instapaper Alternative — Read Later, Organized & Private | onBookmarks"
        description="Looking for an Instapaper alternative? onBookmarks keeps the read-it-later workflow you love — offline reading and a clean reading list — and adds AI tagging, multi-collection organization, and client-side encryption. Import your Instapaper saves in minutes. Free plan available."
        keywords="instapaper alternative, instapaper replacement, read it later app, read later alternative, instapaper vs onbookmarks, import instapaper"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            alternativeHeadline: "Best Instapaper Alternative",
            description:
              "onBookmarks is a read-it-later and bookmark manager: offline reading and a clean reading list, plus AI-powered organization and client-side encryption Instapaper doesn't offer.",
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-primary-subtle fs-normal text-primary px-3 py-2 mb-4">
                  <i className="bi bi-book me-2"></i>
                  Read it later — and actually find it again
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">
                The Instapaper Alternative That Organizes Itself
              </h1>
              <p className="lead mb-4">
                Keep the read-it-later habit you built with Instapaper — offline
                reading and a clean reading list — and add AI tagging,
                multi-collection organization, and client-side encryption on
                top. Bring your saves over in minutes.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Get Started Free
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-primary btn-lg px-4"
                >
                  See the Migration Guide
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                Free forever plan · No credit card required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-wifi-off h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Offline reading</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-shield-lock h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Zero-knowledge encryption</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">AI-powered organization</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why switch */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              Everything You Liked About Instapaper — Plus What It Never Had
            </h2>
            <p className="lead text-muted">
              A read-it-later queue is a great start. Here's what onBookmarks
              adds once your articles pile up.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">AI That Organizes For You</h3>
                  <p className="text-muted mb-3">
                    Instapaper leaves everything in one long queue. onBookmarks
                    reads what you save and files it automatically.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Automatic tag suggestions
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Smart collections by topic
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      No more bottomless reading list
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-shield-lock fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">Privacy Built In</h3>
                  <p className="text-muted mb-3">
                    Instapaper stores your saves in plain text. onBookmarks lets
                    you encrypt sensitive bookmarks on your own device.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Client-side AES-256 encryption
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Zero-knowledge — not even we can read them
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Your data, exportable anytime
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-collection fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">More Than a Reading Queue</h3>
                  <p className="text-muted mb-3">
                    Save any page, not just articles, and organize it across
                    collections and Spaces instead of one flat list.
                  </p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      One bookmark, many collections
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      Separate Spaces for work and life
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      Full-text search across everything
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Read-it-later deep dive — Instapaper's home turf */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-5">
                  <div className="d-flex align-items-start">
                    <div className="icon icon-lg bg-warning-subtle rounded-3 me-3 flex-shrink-0">
                      <i className="bi bi-wifi-off fs-3 text-warning"></i>
                    </div>
                    <div>
                      <h2 className="h4 mb-3">
                        The Read-It-Later Workflow, Kept Intact
                      </h2>
                      <p className="text-muted mb-3">
                        The reason people love Instapaper is simple: save now,
                        read later, even offline. onBookmarks keeps that intact.
                        Your saved articles are cached on your device, so you can
                        read on a plane or in a dead zone, and a built-in reading
                        list lets you mark what you've read to keep your unread
                        queue clean.
                      </p>
                      <p className="text-muted mb-4">
                        The difference is what happens after you save. Instead of
                        an ever-growing list you eventually stop opening, your
                        saves get tagged and sorted automatically — so the
                        article you meant to read is easy to find weeks later.
                      </p>
                      <Link
                        to="/features/offline"
                        className="btn btn-outline-primary"
                      >
                        See How Offline Reading Works
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration Guide */}
      <section id="migration-guide" className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  Move From Instapaper in Three Steps
                </h2>
                <p className="lead text-muted">
                  Your whole reading list, brought over in a couple of minutes.
                </p>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">1</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Export from Instapaper</h4>
                          <p className="text-muted mb-0">
                            In Instapaper, go to Settings and choose Export →
                            Download .CSV. This gives you a file with every
                            article you've saved.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">2</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Create your free account</h4>
                          <p className="text-muted mb-3">
                            Sign up for onBookmarks — no credit card needed — and
                            head to the import screen.
                          </p>
                          <Link
                            to={`${process.env.REACT_APP_APP_URL}/register`}
                            className="btn btn-primary"
                          >
                            Create Free Account
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">3</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">Upload &amp; let AI sort it</h4>
                          <p className="text-muted mb-3">
                            Drop in your export file. We import your saves and our
                            AI suggests tags and collections, so your library
                            arrives organized instead of as one long list.
                          </p>
                          <div className="p-3 rounded-3 bg-success bg-opacity-10 text-success mb-0">
                            <i className="bi bi-check-circle me-2"></i>
                            <span>
                              Duplicate links are flagged during import, so your
                              new library starts clean.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-muted mt-4 mb-0">
                Want the details?{" "}
                <Link to="/features/import">See the full import guide</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  Instapaper vs. onBookmarks
                </h2>
              </div>

              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="p-3">
                        Feature
                      </th>
                      <th scope="col" className="p-3 text-center bg-light">
                        <i className="bi bi-book me-2 text-muted"></i>
                        Instapaper
                      </th>
                      <th
                        scope="col"
                        className="p-3 text-center d-flex align-items-center justify-content-center gap-2"
                      >
                        <LogoOnly width="20" />
                        onBookmarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 fw-medium">Save articles to read later</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Offline reading</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">AI-powered organization</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Full AI Tagging</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Client-side encryption</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-x-circle text-danger"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Zero-Knowledge</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Multi-collection organization</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Folders (paid)</span>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Unlimited &amp; Free</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Full-text search</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Paid tier</span>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Included</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Save any page (not just articles)</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Article-focused</span>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Free unlimited saves</td>
                      <td className="p-3 text-center bg-light">
                        <span className="text-muted">Limited</span>
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Unlimited Free</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="h2 fw-bold mb-4">Instapaper Alternative FAQ</h3>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-6">
                <div className="h-100">
                  <h5 className="fw-bold mb-3">{faq.question}</h5>
                  <p className="text-muted">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h4 className="h1 fw-bold mb-4 text-white">
                Give Your Reading List a Smarter Home
              </h4>
              <p className="lead mb-4">
                Keep reading later — and finally find what you saved. Import your
                Instapaper library and let onBookmarks organize it for you.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  Get Started Free
                </Link>
                <Link
                  to="#migration-guide"
                  className="btn btn-outline-light btn-lg px-4"
                >
                  <i className="bi bi-map me-2"></i>
                  Migration Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrademarkDisclaimer names="Instapaper" />
    </>
  );
};

export default InstapaperAlternative;
